import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Clock, Hourglass, Users, CheckCircle2 } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { api, errDetail, mtlToday, fmtDate } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DayTimeline } from "@/components/home/Availability";

export default function BookingPanel({ dash, onBooked }) {
  const { t, lang, tErr } = useLang();
  const br = dash.booking_rules;
  const days = useMemo(() => Array.from({ length: Math.min(br.advance_window_days + 1, 90) }, (_, i) => mtlToday(i)), [br.advance_window_days]);
  const balance = dash.user.hours_balance || 0;
  const durations = useMemo(() => {
    const out = [];
    for (let d = br.min_session_hours; d <= Math.min(br.max_session_hours, balance); d += 0.5) out.push(d);
    return out;
  }, [br, balance]);
  const [day, setDay] = useState(days[0]);
  const [dur, setDur] = useState(durations[0] || br.min_session_hours || 1);
  const [done, setDone] = useState(null);
  const [av, setAv] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [nextAv, setNextAv] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoadErr(false); setNextAv(null); return api.get("/member/availability", { params: { date: day, duration: dur } }).then((r) => setAv(r.data)).catch(() => { setAv(null); setLoadErr(true); }); };
  useEffect(() => { setSel(null); load(); }, [day, dur]); // eslint-disable-line react-hooks/exhaustive-deps

  const nowFuture = (av?.slots || []).filter((s) => s.available || (!s.free && s.future));
  const full = av && !av.slots.some((s) => s.available);
  const findNext = () => api.get("/member/next-available", { params: { date: day, duration: dur } }).then((r) => setNextAv(r.data)).catch(() => setNextAv({ date: null }));
  const goNext = () => { if (!nextAv?.date) return; setDay(nextAv.date); };
  const endOf = (time) => { const [h, m] = time.split(":").map(Number); const t2 = h * 60 + m + dur * 60; const nd = t2 >= 1440;
    return `${String(Math.floor((t2 % 1440) / 60)).padStart(2, "0")}:${String(t2 % 60).padStart(2, "0")}${nd ? (lang === "fr" ? " (lendemain)" : " (next day)") : ""}`; };

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/member/bookings", { date: day, start_time: sel.time, duration_hours: dur });
      toast.success(t("book.success"));
      setDone({ ...data, day, time: sel.time, end: endOf(sel.time), dur });
      setSel(null);
      await Promise.all([load(), onBooked()]);
    } catch (e) {
      toast.error(tErr(errDetail(e)));
      load();
    } finally { setBusy(false); }
  };
  const wait = async () => {
    setBusy(true);
    try {
      await api.post("/member/waitlist", { date: day, start_time: sel.time, duration_hours: dur });
      toast.success(t("book.waitOk"));
      setSel(null);
      onBooked();
    } catch (e) { toast.error(tErr(errDetail(e))); } finally { setBusy(false); }
  };

  if (done) return (
    <div className="panel brackets mx-auto max-w-xl p-6 text-center sm:p-8" data-testid="booking-confirmation">
      <CheckCircle2 size={44} className="mx-auto mb-3 text-emerald-400" />
      <h3 className="font-display text-3xl uppercase text-white">{lang === "fr" ? "Réservation confirmée" : "Booking confirmed"}</h3>
      <p className="mt-4 capitalize text-white" data-testid="booking-confirmation-date">{fmtDate(done.day, lang)}</p>
      <p className="mt-1 font-mono text-lg text-white" data-testid="booking-confirmation-time">{done.time} → {done.end} · {done.dur} h</p>
      <p className="mt-3 text-sm text-slate-400">{lang === "fr" ? "Un courriel de confirmation vous a été envoyé. La séance apparaît dans « À venir »." : "A confirmation email has been sent. The session appears under “Upcoming”."}</p>
      <button onClick={() => setDone(null)} data-testid="booking-another-btn" className="btn-chrome mt-6 rounded-full px-5 py-2.5 text-sm">{lang === "fr" ? "Réserver une autre séance" : "Book another session"}</button>
    </div>
  );
  if (!durations.length) return <p className="panel p-6 text-amber-200" data-testid="booking-locked-insufficient_hours">{lang === "fr" ? "Votre solde d’heures est insuffisant pour la durée minimale d’une séance." : "Your hour balance is below the minimum session length."}</p>;

  return (
    <div className="grid gap-6 lg:grid-cols-12" data-testid="booking-panel">
      <div className="panel p-5 sm:p-6 lg:col-span-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-2xl uppercase text-white">{t("book.title")}</h3>
          <span className="rounded-full border border-white/10 px-3 py-1 font-mono text-[11px] text-slate-400" data-testid="booking-timezone-label">{t("book.tz")} (America/Toronto)</span>
        </div>
        <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">{t("book.date")}</p>
        <div className="-mx-1 mb-5 flex gap-2 overflow-x-auto px-1 pb-2" data-testid="booking-calendar-grid">
          {days.map((d, i) => (
            <button key={d} onClick={() => setDay(d)} data-testid={`booking-day-${i}`}
              className={`shrink-0 rounded-xl border px-3 py-2 text-sm capitalize transition-colors ${d === day ? "border-red-500 bg-red-600 text-white" : "border-white/10 text-slate-300 hover:border-white/30"}`}>
              {fmtDate(d, lang)}
            </button>
          ))}
        </div>
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-widest text-slate-500">{t("book.duration")}</span>
          <Select value={String(dur)} onValueChange={(v) => setDur(Number(v))}>
            <SelectTrigger className="w-32 border-white/15 bg-black/40" data-testid="booking-duration-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {durations.map((d) => <SelectItem key={d} value={String(d)} data-testid={`booking-duration-${d}`}>{d} {t("book.hours")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DayTimeline busy={av?.busy || []} />
        <p className="mb-2 mt-6 text-xs uppercase tracking-widest text-slate-500">{t("book.start")}</p>
        {loadErr ? (
          <div className="rounded-lg border border-red-500/40 bg-red-950/30 p-4 text-sm text-red-200" data-testid="booking-load-error">
            {lang === "fr" ? "Le calendrier n’a pas pu être chargé. Vérifiez votre connexion." : "The calendar could not be loaded. Check your connection."}
            <button onClick={load} data-testid="booking-retry-btn" className="ml-3 underline">{lang === "fr" ? "Réessayer" : "Retry"}</button>
          </div>
        ) : !av ? <p className="text-sm text-slate-500">{t("book.loading")}</p> : (
          <>
          {full && (
            <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-950/20 p-3 text-sm text-amber-200" data-testid="booking-day-full">
              {lang === "fr" ? "Cette date est complète pour cette durée." : "This date is fully booked for this duration."}
              {!nextAv && <button onClick={findNext} data-testid="booking-find-next-btn" className="ml-2 font-semibold underline">{lang === "fr" ? "Trouver la prochaine disponibilité" : "Find next available slot"}</button>}
              {nextAv?.date && <button onClick={goNext} data-testid="booking-next-available" className="ml-2 font-semibold underline">{fmtDate(nextAv.date, lang)} · {nextAv.time}</button>}
              {nextAv && !nextAv.date && <span className="ml-2">{lang === "fr" ? "Aucune disponibilité dans la fenêtre de réservation." : "No availability in the booking window."}</span>}
            </div>
          )}
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8">
            {nowFuture.map((s) => {
              const active = sel?.time === s.time;
              return (
                <button key={s.time} onClick={() => setSel(s)} data-testid={`booking-time-slot-${s.time.replace(":", "")}`}
                  className={`slot relative rounded-lg border px-2 py-2 font-mono text-sm ${active ? "border-red-500 bg-red-600 text-white" : s.available ? "border-white/10 bg-white/[0.03] text-slate-200 hover:border-white/40" : "border-white/5 bg-red-950/20 text-slate-600 line-through"}`}>
                  {s.time}
                  {s.peak && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-amber-400" title={t("book.peak")} />}
                </button>
              );
            })}
          </div>
          </>
        )}
        <p className="mt-3 flex items-center gap-2 text-xs text-slate-500"><span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> {t("book.peak")} {br.peak_enabled ? `${br.peak_start}–${br.peak_end}` : ""}</p>
      </div>

      <div className="panel brackets h-fit p-5 sm:p-6 lg:col-span-4" data-testid="booking-summary">
        <h4 className="mb-4 font-display text-xl uppercase text-white">{t("book.summary")}</h4>
        {!sel ? <p className="text-sm text-slate-400">{t("book.pick")}</p> : (
          <div className="space-y-3 text-sm">
            <p className="capitalize text-white">{fmtDate(day, lang)}</p>
            <p className="flex items-center gap-2 font-mono text-lg text-white" data-testid="booking-summary-time"><Clock size={16} className="text-red-500" />{sel.time} → {endOf(sel.time)}</p>
            <p className="text-slate-400">{t("book.duration")}: <span className="font-mono text-white">{dur} {t("book.hours")}</span></p>
            <p className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-slate-300" data-testid="booking-guest-limit">
              <Users size={16} className="text-red-500" />{t("book.guests")}: <span className="font-mono text-white">{br.guest_limit != null ? `${br.guest_limit} max.` : t("book.guestsTbc")}</span>
            </p>
            {sel.available ? (
              <>
                <p className="flex justify-between text-slate-400">{t("book.willDeduct")} <span className="font-mono text-white">{dur} h</span></p>
                <p className="flex justify-between text-slate-400">{t("book.balanceAfter")} <span className={`font-mono ${balance - dur < 0 ? "text-red-400" : "text-white"}`}>{(balance - dur).toFixed(1)} h</span></p>
                <button disabled={busy} onClick={book} data-testid="booking-submit-btn" className="btn-red mt-2 h-11 w-full rounded-full font-semibold disabled:opacity-60">{t("book.confirm")}</button>
              </>
            ) : (
              <>
                <p className="text-amber-300">{t("book.unavailable")}</p>
                <button disabled={busy} onClick={wait} data-testid="waitlist-join-btn" className="btn-chrome flex h-11 w-full items-center justify-center gap-2 rounded-full font-semibold"><Hourglass size={16} />{t("book.joinWait")}</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
